import React, { useCallback, useContext } from 'react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import metadata from './ContentsAway.metadata.json5';
import messages from './ContentsAway.messages';
import styles from './ContentsAway.module.scss';
import useCurrency from '../../../hooks/useCurrency';

const IDs = Object.freeze({
    HOMCONTENTSAWAYCOV: 'HOMContentsAwayCov',
    UNSPC_BELONGINGS: 'HOMContentsAwayCoverReqForUnspecBelongings',
    UNSPC_BELONGINGS_REPL_COST: 'HOMContentsAwayCovUnspecifiedBelongingsReplCost',
    UNSPC_PEDAL_CYCLE_LIMIT: 'HOMContentsAwayCovUnspecifiedPedalCyclesLimit',
    SpecifiedPedalCycleExcess: 'HOMContentsAwayCovSpecifiedPedalCycleExcess',
    YES: 'Yes'
});

function ContentsAway(props) {
    const { bicycles, itemsOutsideHome, submissionVM } = props;
    const homeLineCoverages = _.get(submissionVM, 'lobData.value.homeLine.lineCoverages.coverages');
    const contentsAwayCov = homeLineCoverages?.find((cov) => cov.publicID === 'HOMContentsAwayCov');
    const accDamageExcess = _.get(submissionVM.value, 'lobData.homeLine.lineCoverages.accidentaLossOrDamageExcess');
    const theftTotalExcess = _.get(submissionVM.value, 'lobData.homeLine.lineCoverages.theftTotalExcess');
    const unspecifiedContAwayTotalExcess = _.get(submissionVM.value, 'lobData.homeLine.lineCoverages.unspecifiedContAwayTotalExcess');
    const specifiedValuableExcess = _.get(submissionVM.value, 'lobData.homeLine.lineCoverages.specifiedIValuableItemTotalExcess');
    const bicycleExcess = _.get(submissionVM.value, 'lobData.homeLine.lineCoverages.specifiedPedalCycleTotalExcess');

    const translator = useContext(TranslatorContext);
    const getTermValue = (cov, publicId) => {
        const foundTerm = cov?.terms?.find((term) => term.publicID === publicId);
        return foundTerm?.chosenTermValue;
    };
    const isUnspcBelongings =
        contentsAwayCov ? getTermValue(contentsAwayCov, IDs.UNSPC_BELONGINGS) === IDs.YES : false;

    const showUnSpecContAwayExcess = !!unspecifiedContAwayTotalExcess && isUnspcBelongings;
    const showSpecifiedValExcess = !!specifiedValuableExcess && itemsOutsideHome.length > 0;

    const isExcessAvailable = !!accDamageExcess
            || !!theftTotalExcess
            || showUnSpecContAwayExcess
            || !!showSpecifiedValExcess
            || !!bicycleExcess;

    const currencyFormatter = useCurrency();

    const contentsAwayLimit = isUnspcBelongings
        ? `£${getTermValue(contentsAwayCov, IDs.UNSPC_BELONGINGS_REPL_COST)}` : translator(messages.notSelected);

    const bicyclesLimit = isUnspcBelongings
        ? `£${getTermValue(contentsAwayCov, IDs.UNSPC_PEDAL_CYCLE_LIMIT)}` : translator(messages.notSelected);

    const generateBicyclesOverrides = useCallback(() => {
        const coverageItems = bicycles || [];
        const covOverrides = coverageItems.map((changedField, index) => {
            return {
                [`bicycleName${index}`]: {
                    content: changedField.itemData.ArticleType.typeCodeValue
                },
                [`articleLimit${index}`]: {
                    value: currencyFormatter
                        .formatCurrency(changedField.itemData?.ArticleLimit?.bigDecimal, true)
                }
            };
        });
        return Object.assign({}, ...covOverrides);
    }, [bicycles, currencyFormatter]);

    const generateItemsOutsideOverrides = useCallback(() => {
        const coverageItems = itemsOutsideHome || [];
        const covOverrides = coverageItems.map((changedField, index) => {
            const description = changedField?.itemData?.ArticleType?.typeCodeValue;
            const type = changedField?.itemData?.ArticleValuationMethod?.typeCodeValue;
            const serialNumber = changedField?.itemData?.ArticleSerialNumber.typeCodeValue;
            return {
                [`name${index}`]: {
                    content: translator({
                        id: `typekey.SpecifiedValuableType_NFUM.${description}`,
                        defaultMessage: description
                    })
                },
                [`articleType${index}`]: {
                    value: translator({
                        id: `typekey.SpecifiedValuableType_NFUM.${type}`,
                        defaultMessage: type
                    })
                },
                [`serialNumber${index}`]: {
                    value: translator({
                        id: 'quoteandbind.ngh.views.policy-summary.contentsAway.serialNumber',
                        defaultMessage: serialNumber
                    }),
                    visible: !!serialNumber
                },
                [`articleLimit${index}`]: {
                    value: currencyFormatter
                        .formatCurrency(changedField.itemData?.ArticleLimit?.bigDecimal, true)
                }
            };
        });
        return Object.assign({}, ...covOverrides);
    }, [currencyFormatter, itemsOutsideHome, translator]);

    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            className: 'riskObjectPropertyContainer',
            valueClassName: 'riskObjectPropertyValue'
        },
        bicycycleContainer: {
            visible: bicycles.length > 0,
        },
        bicycles: {
            visible: bicycles.length > 0,
            data: bicycles
        },
        contentsAwayLimit: {
            value: contentsAwayLimit
        },
        bicyclesLimit: {
            value: bicyclesLimit
        },
        accidentalDamageExcess: {
            value: `£${accDamageExcess}`,
            visible: !!accDamageExcess
        },
        theftExcess: {
            value: `£${theftTotalExcess}`,
            visible: !!theftTotalExcess
        },
        allOtherUnspecbelongExcess: {
            value: `£${unspecifiedContAwayTotalExcess}`,
            visible: showUnSpecContAwayExcess
        },
        namedItemClaims: {
            value: `£${specifiedValuableExcess}`,
            visible: !!showSpecifiedValExcess
        },
        specifiedBiCyclesExcess: {
            value: `£${bicycleExcess}`,
            visible: !!bicycleExcess
        },
        namedItemsOther: {
            visble: itemsOutsideHome.length > 0,
            data: itemsOutsideHome
        },
        namedItemsOtherContainer: {
            visible: itemsOutsideHome.length > 0,
        },
        contentsAwayExcessContainer: {
            visible: isExcessAvailable
        },
        ...generateBicyclesOverrides(),
        ...generateItemsOutsideOverrides()
    };

    const resolvers = {
        resolveClassNameMap: styles,
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            classNameMap={resolvers.resolveClassNameMap}
            overrideProps={overrideProps}
        />
    );
}

export default ContentsAway;
